import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { mountGoogleAds } from "@/lib/ads";
import { installProtection } from "@/lib/protection";

import AppShell from "@/components/AppShell";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollManager from "@/components/ScrollManager";

import HomePage from "@/pages/HomePage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import DashboardPage from "@/pages/DashboardPage";
import JobsPage from "@/pages/JobsPage";
import JobDetailPage from "@/pages/JobDetailPage";
import CompaniesPage from "@/pages/CompaniesPage";
import NewsPage from "@/pages/NewsPage";
import KarmpatraListPage from "@/pages/KarmpatraListPage";
import KarmpatraIssuePage from "@/pages/KarmpatraIssuePage";
import ProfilePage from "@/pages/ProfilePage";
import ApplicationsPage from "@/pages/ApplicationsPage";
import SavedJobsPage from "@/pages/SavedJobsPage";
import ContentStudioPage from "@/pages/ContentStudioPage";
import PricingPage from "@/pages/PricingPage";
import AdminPage from "@/pages/AdminPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminEmailPage from "@/pages/AdminEmailPage";
import AdminLocationsPage from "@/pages/AdminLocationsPage";
import AdminConsolePage from "@/pages/AdminConsolePage";
import AdminDedupLogPage from "@/pages/AdminDedupLogPage";
import SettingsPage from "@/pages/SettingsPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsPage from "@/pages/TermsPage";
import ContactPage from "@/pages/ContactPage";

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  // Reinstall / uninstall protection when the auth state changes.
  // Staff/admin/recruiter get devtools + copy; everyone else gets the friction layer.
  useEffect(() => { installProtection(user); }, [user]);
  // CRITICAL: Detect session_id during render (synchronously) to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <AppShell>
      <ScrollManager />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/jobs" element={<JobsPage />} />
        <Route path="/jobs/:id" element={<JobDetailPage />} />
        <Route path="/companies" element={<CompaniesPage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/karmpatra" element={<KarmpatraListPage />} />
        <Route path="/karmpatra/:slug" element={<KarmpatraIssuePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/applications" element={<ProtectedRoute><ApplicationsPage /></ProtectedRoute>} />
        <Route path="/saved" element={<ProtectedRoute><SavedJobsPage /></ProtectedRoute>} />
        <Route path="/content-studio" element={<ProtectedRoute><ContentStudioPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute><AdminUsersPage /></ProtectedRoute>} />
        <Route path="/admin/email" element={<ProtectedRoute><AdminEmailPage /></ProtectedRoute>} />
        <Route path="/admin/locations" element={<ProtectedRoute><AdminLocationsPage /></ProtectedRoute>} />
        <Route path="/admin/console" element={<ProtectedRoute><AdminConsolePage /></ProtectedRoute>} />
        <Route path="/admin/dedup-log" element={<ProtectedRoute><AdminDedupLogPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/contact" element={<ContactPage />} />
      </Routes>
    </AppShell>
  );
}

function App() {
  useEffect(() => { mountGoogleAds(); }, []);
  useEffect(() => {
    if (window.history.scrollRestoration) window.history.scrollRestoration = "manual";
  }, []);
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
